import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useContext, useEffect, useState } from "react";
import {
  DayOfficeDetailProps,
  PropertyDetailType,
} from "../../configs/DeclareType";
import { dayOfficeDetailThunk } from "../../thunks/BookThunks";
import { BOOK_DAY_OFFICE_LINK } from "../../../App/configs/ListRoutes";
import MasterTemplate from "../../../App/views/MasterTemplate";
import { Dimmer, Loader } from "semantic-ui-react";
import SectionWhiteBox from "../../../../components/SectionWhiteBox";
import Thumbnail from "../BookProperty/Component/Thumbnail";
import Amenities from "../BookProperty/Component/Amenities";
import Copy from "../BookProperty/Component/Copy";
import Rating from "../BookProperty/Component/Rating";
import BookingBoxDayOffice from "./Component/BookingBox";
import {
  clipboardCopyWithAsyncFunction,
  shareDayOfficeMessage,
} from "../../services/Common";
import { toastSuccess } from "../../../../components/Toast";
import { LocalizationContext } from "../../../../locales/Translation";
import { propertyDetailViewService } from "../../services/BookServices";
import {
  ROLE_BUSINESS_ADMIN_ID,
  ROLE_BUSINESS_USER_ID,
} from "../../../User/configs/Constants";
import Address from "../BookProperty/Component/Address";
import Schedule from "../BookProperty/Component/Schedule";
import { dayOfficeDetailDataSelector } from "../../selectors/BookSelectors";
import ModalBookingSubmittedDayOffice from "../Components/ModalBookingSubmittedDayOffice";
import DayOfficeSuggestion from "../BookProperty/Component/DayOfficeSuggestion";

const BookDayOffice = () => {
  const { translations } = useContext(LocalizationContext);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);

  const dayOfficeDetailData: DayOfficeDetailProps = useSelector(
    dayOfficeDetailDataSelector
  );

  const [propertyData, setPropertyData] = useState<PropertyDetailType | null>(
    null
  );

  useEffect(() => {
    (async () => {
      try {
        if (!id) {
          return;
        }
        setLoading(true);
        await dispatch<any>(dayOfficeDetailThunk(id));
      } catch (e) {
        navigate(BOOK_DAY_OFFICE_LINK);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        if (!dayOfficeDetailData?.property_id || !id) {
          return;
        }
        const body = document.querySelector("body");
        if (body) {
          body.classList.remove("prevent-scroll");
        }
        const { data } = await propertyDetailViewService(
          String(dayOfficeDetailData?.property_id)
        );
        setPropertyData(data);
      } catch (e) {
        setPropertyData(null);
      }
    })();
  }, [dayOfficeDetailData]);

  const _share = () =>
    shareDayOfficeMessage(
      translations,
      dayOfficeDetailData.day_office_id,
      dayOfficeDetailData.web_url
    );

  return (
    <MasterTemplate
      roleAccept={[ROLE_BUSINESS_USER_ID, ROLE_BUSINESS_ADMIN_ID]}
    >
      {loading ? (
        <Dimmer active>
          <Loader />
        </Dimmer>
      ) : (
        <>
          <SectionWhiteBox isShowBack backRoute={BOOK_DAY_OFFICE_LINK}>
            <div className={"book-detail__name-wrapper"}>
              <h2 className={"book-detail__name"}>
                {dayOfficeDetailData?.name}
              </h2>
            </div>
            <div className={"book-detail__description-wrapper"}>
              <div className={"book-detail__description"}>
                {propertyData ? (
                  <span>
                    <i className="uil uil-star" />
                    {propertyData.rating.star} ({propertyData.rating.total}{" "}
                    {propertyData.rating.total < 2 ? "review" : "reviews"})
                  </span>
                ) : null}
                <span className={"dot"}>•</span>
                <span>
                  <i className="uil uil-building" />
                  {dayOfficeDetailData.company_name}
                </span>
                <span className={"dot"}>•</span>
                <span>
                  <i className="uil uil-map" />
                  {dayOfficeDetailData.address}
                </span>
                <span className={"dot"}>•</span>
                <span>
                  <i className="uil uil-users-alt" />
                  {dayOfficeDetailData.capacity}{" "}
                  {dayOfficeDetailData.capacity > 1 ? "People" : "Person"}
                </span>
              </div>
              <button
                className={
                  "book-detail__description book-detail__description-share"
                }
                onClick={async () => {
                  clipboardCopyWithAsyncFunction(_share);
                  toastSuccess(translations.copyToClipboard);
                }}
              >
                <i className="uil uil-upload"></i>
                &nbsp;&nbsp;Share
              </button>
            </div>
            <Thumbnail thumbnail={dayOfficeDetailData.images} />
            <div className={"book-detail__book-section"}>
              <div>
                <Amenities amenities={dayOfficeDetailData.amenities} />
                <Schedule
                  propertyDetail={dayOfficeDetailData}
                  type={"DAY_OFFICE"}
                />
                <Copy
                  title={"Booking Requirements"}
                  content={dayOfficeDetailData.booking_requirement}
                />
                <Copy
                  title={"Cancellation Policy"}
                  content={dayOfficeDetailData.cancel_policy}
                />
                {propertyData ? <Rating rating={propertyData.rating} /> : null}
              </div>
              <div>
                <BookingBoxDayOffice />
              </div>
            </div>
            <Address
              lat={dayOfficeDetailData.lat}
              lng={dayOfficeDetailData.lng}
            />
            <DayOfficeSuggestion
              dayOffices={
                propertyData
                  ? propertyData?.day_offices.filter(
                      ({ day_office_id: dayOfficeId }) =>
                        dayOfficeId !== Number(id)
                    )
                  : []
              }
            />
          </SectionWhiteBox>
          <ModalBookingSubmittedDayOffice />
        </>
      )}
    </MasterTemplate>
  );
};

export default BookDayOffice;
